import {
  GoogleOneTapSettings,
  promptDelayMs,
  shouldLoadOneTap,
} from '@common/auth/one-tap/one-tap-rules';
import {useHandleLoginSuccess} from '@common/auth/requests/use-login';
import {RequestPasswordDialogContent} from '@common/auth/ui/social-auth-section';
import {useAuth} from '@common/auth/use-auth';
import {apiClient} from '@common/http/query-client';
import {Button} from '@shadcn/button/button';
import {Dialog} from '@shadcn/dialog/dialog';
import {toast} from '@shadcn/toast/toast';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {Trans} from '@ui/i18n/trans';
import {useSettings} from '@ui/settings/use-settings';
import {XIcon} from 'lucide-react';
import {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

interface GsiApi {
  initialize: (config: Record<string, unknown>) => void;
  prompt: (listener?: (n: any) => void) => void;
  renderButton: (el: HTMLElement, options: Record<string, unknown>) => void;
  cancel: () => void;
}

function gsi(): GsiApi | undefined {
  return (window as any).google?.accounts?.id;
}

let scriptPromise: Promise<void> | null = null;
function loadGsiScript(): Promise<void> {
  if (gsi()) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = GSI_SRC;
    el.async = true;
    el.defer = true;
    el.dataset.testid = 'google-gsi-script';
    el.onload = () => resolve();
    el.onerror = () => {
      scriptPromise = null;
      el.remove();
      reject(new Error('gsi'));
    };
    document.head.appendChild(el);
  });
  return scriptPromise;
}

interface OneTapResponse {
  status: string;
  two_factor?: boolean;
  bootstrapData?: string;
  message?: string;
}

/**
 * Google One Tap. Loads Google Identity Services only when enabled by the
 * admin, the visitor is logged out, the host is allowed and the page is a
 * public or auth page. Google decides if and how the popup is shown.
 */
export function GoogleOneTap() {
  const {social, html_base_uri} = useSettings();
  const settings = social?.google?.one_tap as GoogleOneTapSettings | undefined;
  const {isLoggedIn} = useAuth();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const handleLoginSuccess = useHandleLoginSuccess();
  const [showFallback, setShowFallback] = useState(false);
  const [requestPassword, setRequestPassword] = useState(false);
  const fallbackRef = useRef<HTMLDivElement>(null);
  const initializedFor = useRef<string | null>(null);

  const enabled = shouldLoadOneTap({
    settings,
    isLoggedIn,
    host: window.location.host,
    pathname,
    basePath: html_base_uri,
    isPreview:
      !!(getBootstrapData() as {is_settings_preview?: boolean})
        .is_settings_preview || window.self !== window.top,
  });
  const clientId = settings?.resolved_client_id || '';

  const handleSuccess = useRef(handleLoginSuccess);
  handleSuccess.current = handleLoginSuccess;

  useEffect(() => {
    if (!enabled || !clientId) return;
    let cancelled = false;
    let timer: number | undefined;

    const onCredential = async (response: {credential?: string}) => {
      if (!response?.credential) return;
      try {
        const {data} = await apiClient.post<OneTapResponse>(
          'api/v1/auth/google/one-tap',
          {credential: response.credential},
        );
        if (data.status === 'two_factor') {
          navigate('/login', {state: {twoFactor: true}});
        } else if (data.status === 'request_password') {
          setRequestPassword(true);
        } else if (data.bootstrapData) {
          handleSuccess.current({
            bootstrapData: data.bootstrapData,
            two_factor: false,
          });
        }
      } catch (e: any) {
        toast.error(
          e?.response?.data?.message || (
            <Trans message="Google sign-in could not be verified." />
          ),
        );
      }
    };

    loadGsiScript()
      .then(() => {
        const api = gsi();
        if (cancelled || !api) return;
        if (initializedFor.current !== clientId) {
          api.initialize({
            client_id: clientId,
            callback: onCredential,
            auto_select: false,
            cancel_on_tap_outside: settings?.cancel_on_tap_outside ?? true,
            context: settings?.context || 'signin',
            itp_support: true,
            use_fedcm_for_prompt: true,
          });
          initializedFor.current = clientId;
        }
        if (settings?.auto_show === false) {
          if (settings?.fallback_button !== false) setShowFallback(true);
          return;
        }
        timer = window.setTimeout(() => {
          api.prompt(notification => {
            if (settings?.fallback_button === false) return;
            const notShown =
              notification?.isNotDisplayed?.() ||
              notification?.isSkippedMoment?.();
            if (notShown) setShowFallback(true);
          });
        }, promptDelayMs(settings?.prompt_delay));
      })
      .catch(() => {
        // Blocked by an extension or the network: nothing to show.
      });

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      gsi()?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, clientId, pathname]);

  useEffect(() => {
    if (!enabled) setShowFallback(false);
  }, [enabled]);

  useEffect(() => {
    const api = gsi();
    if (showFallback && fallbackRef.current && api) {
      fallbackRef.current.innerHTML = '';
      api.renderButton(fallbackRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: settings?.context === 'signup' ? 'signup_with' : 'signin_with',
        shape: 'pill',
        locale: document.documentElement.lang || undefined,
      });
    }
  }, [showFallback, settings?.context]);

  if (!enabled) return null;

  return (
    <>
      {showFallback ? (
        <div
          className="fixed end-16 bottom-16 z-50 flex items-center gap-4 rounded-panel border bg-background p-8 shadow-lg"
          data-testid="google-one-tap-fallback"
        >
          <div ref={fallbackRef} />
          <Button
            size="icon-xs"
            variant="ghost"
            onClick={() => setShowFallback(false)}
            aria-label="Close"
          >
            <XIcon />
          </Button>
        </div>
      ) : null}
      <Dialog.Root open={requestPassword} onOpenChange={setRequestPassword}>
        <Dialog.Portal>
          <Dialog.Backdrop />
          <RequestPasswordDialogContent
            onClose={() => setRequestPassword(false)}
          />
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
